import { useRef, useState } from 'react';
import { Upload, Loader2, X, ShieldCheck, ArrowUp, ArrowDown, AlertTriangle, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { absoluteImageUrl } from './ImageUploader';

const MAX_BYTES = 8 * 1024 * 1024;
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * GalleryUploader
 * value: [{ url, alt, gps_removed? }, ...]
 * onChange: called with the new array
 *
 * Accepts multiple files at once. Uploads them in parallel through
 * /api/admin/uploads (which strips EXIF including GPS). Shows a grid of
 * previews with per-item alt text, reorder controls, remove, and a
 * "GPS removed" pill when applicable.
 */
export const GalleryUploader = ({ value = [], onChange, label = 'Gallery' }) => {
  const fileRef = useRef(null);
  const [pending, setPending] = useState([]); // [{ id, name, status: 'uploading'|'error', error? }]
  const [dragOver, setDragOver] = useState(false);

  const items = Array.isArray(value) ? value : [];

  const patchItems = (next) => onChange(next);

  const uploadOne = async (file, tempId) => {
    if (!ALLOWED.includes(file.type)) {
      return { tempId, error: `Unsupported: ${file.name}` };
    }
    if (file.size > MAX_BYTES) {
      return { tempId, error: `${file.name} is larger than 8 MB` };
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/uploads', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { tempId, ok: true, data: res.data, name: file.name };
    } catch (err) {
      return { tempId, error: err?.response?.data?.detail || 'Upload failed' };
    }
  };

  const uploadMany = async (fileList) => {    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const tempIds = files.map((_, i) => `p-${Date.now()}-${i}`);
    setPending((cur) => [
      ...cur,
      ...files.map((f, i) => ({ id: tempIds[i], name: f.name, status: 'uploading' })),
    ]);

    const results = await Promise.all(files.map((f, i) => uploadOne(f, tempIds[i])));

    const newItems = [];
    let anyGps = false;
    let successCount = 0;
    for (const r of results) {
      if (r.ok) {
        newItems.push({
          url: r.data.url,
          alt: '',
          gps_removed: Boolean(r.data.gps_removed),
        });
        anyGps = anyGps || Boolean(r.data.gps_removed);
        successCount += 1;
      }
    }
    if (newItems.length) patchItems([...(items || []), ...newItems]);

    const errors = results.filter((r) => r.error);
    setPending((cur) => cur.filter((p) => !tempIds.includes(p.id)));
    if (successCount) {
      toast.success(
        `${successCount} image${successCount === 1 ? '' : 's'} added${anyGps ? ' \u2014 EXIF GPS removed' : ''}.`,
      );
    }
    for (const e of errors) toast.error(e.error);

    if (fileRef.current) fileRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) uploadMany(e.dataTransfer.files);
  };

  const removeAt = (i) => patchItems(items.filter((_, idx) => idx !== i));
  const addByUrl = (raw) => {
    const url = (raw || '').trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Enter a full http(s) URL.');
      return;
    }
    patchItems([...(items || []), { url, alt: '' }]);
    toast.success('Added by URL. External images are not sanitised.');
  };  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    patchItems(next);
  };
  const setAlt = (i, alt) => {
    const next = items.slice();
    next[i] = { ...next[i], alt };
    patchItems(next);
  };
  const clearAll = () => {
    if (!items.length) return;
    if (!window.confirm('Remove all gallery images?')) return;
    patchItems([]);
  };

  const anyMissingAlt = items.some((it) => !it.alt || it.alt.trim().length === 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="kicker">{label}</Label>
        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
          <span>
            {items.length} image{items.length === 1 ? '' : 's'}
            {pending.length > 0 && ` \u00b7 ${pending.length} uploading`}
          </span>
          {items.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs underline hover:text-[hsl(var(--foreground))]"
            >
              Remove all
            </button>
          )}
        </div>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
          dragOver
            ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.05)]'
            : 'border-[hsl(var(--border))] bg-[hsl(var(--secondary)/0.4)]'
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => uploadMany(e.target.files)}
        />
        <div className="flex flex-col items-center gap-2">
          <div className="h-9 w-9 rounded-full bg-[hsl(var(--primary)/0.1)] flex items-center justify-center">
            {pending.length > 0 ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Upload size={16} />
            )}
          </div>
          <p className="text-sm font-medium">
            {pending.length > 0
              ? `Uploading ${pending.length} image${pending.length === 1 ? '' : 's'}\u2026`
              : 'Drop images here or choose files'}
          </p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] max-w-md">
            Add several at once. JPEG, PNG, or WebP up to 8 MB each. EXIF (including GPS) is removed automatically before storage.
          </p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => fileRef.current?.click()}
            disabled={pending.length > 0}
            className="mt-1"
          >
            Choose files
          </Button>
        </div>
      </div>

      <details className="text-xs">
        <summary className="cursor-pointer text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1">
          <LinkIcon size={12} /> Or add an image by URL
        </summary>
        <div className="mt-2 flex gap-2">
          <Input
            type="url"
            placeholder="https://…"
            className="h-9"
            data-testid="gallery-url-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addByUrl(e.currentTarget.value);
                e.currentTarget.value = '';
              }
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            data-testid="gallery-url-add"
            onClick={(e) => {
              const inp = e.currentTarget.parentElement.querySelector('input[type="url"]');
              addByUrl(inp?.value);
              if (inp) inp.value = '';
            }}
          >
            Add
          </Button>
        </div>
      </details>

      {anyMissingAlt && items.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-[hsl(45_90%_50%/0.35)] bg-[hsl(45_90%_50%/0.08)] px-3 py-2 text-xs text-[hsl(38_65%_28%)]">
          <AlertTriangle size={14} />
          <span>Add alt text on each image for accessibility (describe what is in the photo).</span>
        </div>
      )}

      {(items.length > 0 || pending.length > 0) && (
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((img, i) => (
            <div
              key={`${img.url}-${i}`}
              className="group rounded-xl overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--card))]"
            >
              <div className="relative aspect-[4/3] bg-[hsl(var(--muted))]">
                <img
                  src={absoluteImageUrl(img.url)}
                  alt={img.alt || `Gallery image ${i + 1}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                {img.gps_removed && (
                  <span
                    className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/70 text-white text-[10px] px-2 py-0.5"
                    title="EXIF GPS was found and removed before storage"
                  >
                    <ShieldCheck size={10} /> GPS removed
                  </span>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    aria-label="Move up"
                    className="h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 disabled:opacity-40"
                  >
                    <ArrowUp size={11} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === items.length - 1}
                    aria-label="Move down"
                    className="h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 disabled:opacity-40"
                  >
                    <ArrowDown size={11} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    aria-label="Remove image"
                    className="h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                  >
                    <X size={11} />
                  </button>
                </div>
              </div>
              <div className="p-2">
                <Input
                  value={img.alt || ''}
                  onChange={(e) => setAlt(i, e.target.value)}
                  placeholder="Alt text"
                  className="h-8 text-xs"
                />
              </div>
            </div>
          ))}

          {pending.map((p) => (
            <div
              key={p.id}
              className="rounded-xl overflow-hidden border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.5)] aspect-[4/3] flex flex-col items-center justify-center text-xs text-[hsl(var(--muted-foreground))] p-3"
            >
              <Loader2 size={16} className="animate-spin mb-2" />
              <span className="line-clamp-1 max-w-full">{p.name}</span>
              <span className="mt-1">Stripping EXIF…</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
